import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, X, Phone } from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navigationItems = [
    { label: 'Accueil', href: '#hero' },
    { label: 'Services', href: '#services' },
    { label: 'Savoir-faire', href: '#about' },
    { label: 'Témoignages', href: '#testimonials' },
    { label: 'Contact', href: '#contact' }
  ];
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'backdrop-blur-xl bg-[#FAFAF9]/90 border-b border-[#E8D5D1]/40 shadow-sm' 
          : 'bg-transparent'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="cursor-pointer" onClick={() => scrollToSection('#hero')} data-testid="header-logo">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/e29e1e74-dfad-4ff0-a54e-5cee69b74787/images/bdeeb968c67a238c88bf066835fa5e9366205e516d5318d85119a91bd27eb1b4.png"
              alt="Baya Couture"
              className="h-12 w-auto hover:scale-105 transition-transform duration-300"
            />
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            {navigationItems.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className={`text-sm tracking-[0.08em] uppercase font-medium transition-colors duration-300 ${
                  scrolled ? 'text-[#57534E] hover:text-[#D4AF37]' : 'text-[#57534E] hover:text-[#D4AF37]'
                }`}
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            ))}
          </nav>
          
          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:0149100388" className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#D4AF37] transition-colors" data-testid="header-phone">
              <Phone className="w-4 h-4" />
              <span style={{ fontFamily: "'Outfit', sans-serif" }}>01 49 10 03 88</span>
            </a>
            <Button 
              onClick={() => scrollToSection('#contact')}
              size="sm"
              className="bg-[#D4AF37] hover:bg-[#B8962E] text-white font-medium px-6 rounded-full tracking-wide transition-all duration-300"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="header-contact-btn"
            >
              Nous contacter
            </Button>
          </div>
          
          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6 text-[#1C1917]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 bg-[#FAFAF9] border-l border-[#E8D5D1]/30">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between py-4 border-b border-[#E8D5D1]/30">
                    <span className="text-lg font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>Menu</span>
                    <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="p-2">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  <nav className="flex flex-col py-8 gap-1">
                    {navigationItems.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.href)}
                        className="text-left text-lg text-[#57534E] hover:text-[#D4AF37] py-3 px-2 transition-colors border-b border-[#E8D5D1]/20"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </nav>
                  <div className="mt-auto space-y-3 pb-8">
                    <a href="tel:0149100388" className="flex items-center justify-center gap-2 w-full py-3 border border-[#D4AF37] rounded-full text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                      <Phone className="w-4 h-4" />
                      01 49 10 03 88
                    </a>
                    <Button onClick={() => scrollToSection('#contact')} className="w-full bg-[#D4AF37] hover:bg-[#B8962E] text-white rounded-full">
                      Nous contacter
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
